/**
 * Katching Bundles <-> Dawn-family media-gallery compatibility bridge.
 *
 * Problem:
 *   Katching Bundles renders its own variant picker (native <select> and/or a custom
 *   "swatch dropdown") instead of the theme's native variant picker. The theme's own
 *   product-info.js only updates the featured image in response to its own internal
 *   pub/sub signal, which Katching's picker never triggers, so the featured image never
 *   follows the selected color/size.
 *
 * Fix (v3 - decoupled from Katching's internal event contract, theme-layout agnostic):
 *   Two independent triggers feed the same sync logic:
 *     1. The native browser "change" event on any Katching <select> option control
 *        (guaranteed to fire for select-based options like Size) - the variant is
 *        resolved from the full set of currently selected option values, matched
 *        against the product's real variant list.
 *     2. Katching's own "variant-selected"/"variants-changed" custom events (needed for
 *        swatch-style color pickers, which aren't <select> elements at all and never
 *        fire a native "change") - used directly when they carry a variant id.
 *
 *   Once a variant id is known, re-fetch the section HTML via the same Section Rendering
 *   API call the theme's own product-info.js makes, swap in the freshly-rendered
 *   <media-gallery> markup (so "hide variant images" is respected), then call the
 *   gallery's own public setActiveMedia() method - the same method the theme calls
 *   internally.
 *
 *   Layout note: some themes (Dawn) nest <media-gallery> inside <product-info>; others
 *   (e.g. Shrine) render them as siblings under the shared section wrapper. This bridge
 *   looks up the gallery by scoping to the enclosing Shopify section element
 *   (`#<section-id>`) rather than assuming any particular nesting, so it works either
 *   way.
 *
 * This file does not modify any theme core file. It's additive: it only reacts to
 * events on Katching's own elements and touches the <media-gallery> element already
 * on the page.
 */
(function () {
  'use strict';

  function debugWarn() {
    if (window.__kachingDawnBridgeDebug) {
      console.warn.apply(console, ['[Kaching/Dawn media bridge]'].concat(Array.prototype.slice.call(arguments)));
    }
  }

  function getProductInfoFor(el) {
    return el.closest('product-info') || document.querySelector('product-info');
  }

  // The gallery isn't always a descendant of <product-info> (varies by theme), so scope
  // the lookup to the shared Shopify section wrapper instead, falling back to
  // product-info itself and finally the whole document.
  function getSectionScope(productInfo, sectionId) {
    return (
      (sectionId && document.getElementById(sectionId)) ||
      (sectionId && document.getElementById('shopify-section-' + sectionId)) ||
      productInfo ||
      document
    );
  }

  function findVariantSelectsInSameBar(changedSelect) {
    // Scope to the containing <div> whose class marks the current bundle "bar" (e.g.
    // "kaching-bundles__bar-variant-selects"). Deliberately restricted to <div> so this
    // doesn't accidentally match the changed <select> itself, whose own class name
    // ("kaching-bundles__bar-variant-select") also contains the "kaching-bundles__bar"
    // substring.
    var bar =
      changedSelect.closest('div[class*="kaching-bundles__bar"]') ||
      changedSelect.closest('kaching-bundles-block') ||
      document;
    return Array.prototype.slice.call(bar.querySelectorAll('select.kaching-bundles__bar-variant-select'));
  }

  function resolveVariantFromSelects(selects, product) {
    if (!product || !product.options || !product.variants) return null;

    // Map each select's option name (Color/Size/...) to its currently chosen value.
    var selectedByName = {};
    selects.forEach(function (select) {
      var name = select.dataset && select.dataset.name;
      if (!name) return;
      selectedByName[name] = select.value;
    });

    if (!Object.keys(selectedByName).length) return null;

    // product.options is ordered; variant.option1/option2/option3 follow that same order.
    var orderedNames = product.options.map(function (opt) {
      return typeof opt === 'string' ? opt : opt.name;
    });

    return (
      product.variants.filter(function (variant) {
        return orderedNames.every(function (name, index) {
          if (!(name in selectedByName)) return true; // this select wasn't part of the change
          var key = 'option' + (index + 1);
          return variant[key] === selectedByName[name];
        });
      })[0] || null
    );
  }

  function syncFeaturedImage(productInfo, requestUrl, sectionId, variantId, token, isStillCurrent) {
    var sectionFetch = fetch(
      requestUrl + '?variant=' + encodeURIComponent(variantId) + '&section_id=' + encodeURIComponent(sectionId)
    ).then(function (response) {
      if (!response.ok) throw new Error('Section request failed with status ' + response.status);
      return response.text();
    });

    var productJsonFetch = fetch(requestUrl + '.js', { cache: 'no-store' }).then(function (response) {
      if (!response.ok) throw new Error('Product JSON request failed with status ' + response.status);
      return response.json();
    });

    Promise.all([sectionFetch, productJsonFetch])
      .then(function (results) {
        if (!isStillCurrent()) return; // a newer selection came in while this was in flight

        var html = results[0];
        var product = results[1];

        var variant = product.variants.filter(function (v) {
          return String(v.id) === String(variantId);
        })[0];
        var featuredMediaId = variant && variant.featured_media && variant.featured_media.id;

        var scope = getSectionScope(productInfo, sectionId);
        var currentGallery = scope.querySelector('media-gallery') || document.querySelector('media-gallery');
        if (!currentGallery) {
          debugWarn('No <media-gallery> found on the page; skipping.');
          return;
        }

        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newGallery =
          (currentGallery.id && doc.getElementById(currentGallery.id)) || doc.querySelector('media-gallery');

        if (newGallery) {
          currentGallery.replaceWith(newGallery);
          currentGallery = newGallery;
        } else {
          debugWarn('Re-rendered section did not contain a <media-gallery>; leaving current gallery in place.');
        }

        if (featuredMediaId && typeof currentGallery.setActiveMedia === 'function') {
          var mediaDomId = sectionId + '-' + featuredMediaId;
          if (currentGallery.querySelector('[data-media-id="' + mediaDomId + '"]')) {
            currentGallery.setActiveMedia(mediaDomId, true);
          } else {
            debugWarn('Variant featured media not found in re-rendered gallery:', mediaDomId);
          }
        } else {
          debugWarn('No featured_media id resolved for variant', variantId);
        }
      })
      .catch(function (err) {
        debugWarn('Failed to sync featured image for variant', variantId, err);
      });
  }

  function bindBlock(block) {
    if (block.__kachingDawnBridgeBound) return;
    block.__kachingDawnBridgeBound = true;

    var productInfo = getProductInfoFor(block);
    if (!productInfo) {
      debugWarn('No <product-info> element found for this Katching bundle block; skipping.');
      return;
    }

    var requestUrl = productInfo.dataset.url;
    var sectionId = productInfo.dataset.section;
    if (!requestUrl || !sectionId) {
      debugWarn('product-info is missing data-url/data-section; skipping.');
      return;
    }

    var latestToken = 0;

    function triggerSync(variantId) {
      if (!variantId) return;
      var token = ++latestToken;
      syncFeaturedImage(productInfo, requestUrl, sectionId, variantId, token, function () {
        return token === latestToken;
      });
    }

    // Trigger 1: react to the native "change" event on Katching's own <select> controls
    // (e.g. Size). Guaranteed to fire - doesn't depend on any custom event.
    block.addEventListener('change', function (event) {
      var select = event.target;
      if (!select || !select.matches || !select.matches('select.kaching-bundles__bar-variant-select')) return;

      fetch(requestUrl + '.js', { cache: 'no-store' })
        .then(function (response) {
          if (!response.ok) throw new Error('Product JSON request failed with status ' + response.status);
          return response.json();
        })
        .then(function (product) {
          var selects = findVariantSelectsInSameBar(select);
          var variant = resolveVariantFromSelects(selects, product);
          if (!variant) {
            debugWarn('Could not resolve a matching variant from current select values.');
            return;
          }
          triggerSync(variant.id);
        })
        .catch(function (err) {
          debugWarn('Failed to resolve variant from native change event', err);
        });
    });

    // Trigger 2: Katching's own custom events. Required for swatch-style pickers (e.g.
    // color swatches rendered as clickable divs, not a <select>), which never fire a
    // native "change" event at all.
    block.addEventListener('variant-selected', function (event) {
      var variantId = event && event.detail && event.detail.variantId;
      triggerSync(variantId);
    });
    block.addEventListener('variants-changed', function (event) {
      var entries = event && event.detail && event.detail.variantIdQuantities;
      var variantId = entries && entries[0] && entries[0].variantId;
      triggerSync(variantId);
    });
  }

  function init() {
    var blocks = document.querySelectorAll('kaching-bundles-block');
    blocks.forEach(bindBlock);

    // Katching's bundle block can be (re)rendered after its own async init (e.g. after
    // fetching bundle config), so keep watching for late-arriving blocks too.
    var observer = new MutationObserver(function () {
      document.querySelectorAll('kaching-bundles-block').forEach(bindBlock);
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();